import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import posthog from 'posthog-js';
import * as api from '../../services/api';
import { isInSaaSMode } from '../../utils/common';
import { analytics, Map } from './analytics';

const API_KEY = 'phc_56XBEzZmh02mGkadqLiYW51eECyYKWPyecVwkGdGUfg';
const API_HOST = 'https://app.posthog.com';

const POSTHOG_PROPERTIES = {
  api_host: API_HOST,
};
const NO_RECORDING_POSTHOG_PROPERTIES = {
  ...POSTHOG_PROPERTIES,
  // Couldn't find another working way to disable the recording.
  api_transport: 'sendBeacon',
  disable_session_recording: true,
};

type SendTrackingEventProps = {
  eventName: string;
  properties?: any;
};

type InitTrackingProps = {
  groupType: string;
  groupKey: string;
  registerParams?: { [key: string]: string | undefined };
  userIdentifier?: string;
  userParams?: { [key: string]: string | undefined };
  shouldRecord?: boolean;
};

export type TrackingType = {
  sendTrackingEvent: (...args: any[]) => void;
  initTracking: (...args: any[]) => void;
  disableTracking: () => void;
  enableTracking: () => void;
  isTrackingEnabled: boolean;
  analytics: { [key: string]: Map };
};

const useTracking = () => {
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);
  const [envId, setEnvId] = useState<string>();

  const disableTracking = () => setIsTrackingEnabled(false);
  const enableTracking = () => setIsTrackingEnabled(true);

  const { mutate: sendTrackingEventMutation } = useMutation(
    (data: { eventName: string; properties: any }) =>
      api.sendTrackingEvent(data, envId!),
  );

  const initTracking = ({
    groupType,
    groupKey,
    registerParams,
    userIdentifier,
    userParams,
    shouldRecord = false,
  }: InitTrackingProps) => {
    // disable tracking for impersonate users
    const shadowRootContainer = document.getElementById(
      'frontegg-login-box-container-default',
    );
    const shadowRootElement = shadowRootContainer?.shadowRoot;

    // Disable tracking for local development or when a user is being impersonated
    if (
      window.location.hostname === 'localhost' ||
      shadowRootElement?.querySelector('.impersonate-container') ||
      !isInSaaSMode()
    ) {
      return;
    }

    const properties = shouldRecord
      ? POSTHOG_PROPERTIES
      : NO_RECORDING_POSTHOG_PROPERTIES;

    posthog.init(API_KEY, properties);
    posthog.group(groupType, groupKey);
    if (registerParams) {
      posthog.register(registerParams);
      setEnvId(registerParams.environment_id!);
    }

    if (userIdentifier) {
      const params = userParams ?? {};
      posthog.identify(userIdentifier, params);
    }
    enableTracking();
  };

  const sendTrackingEvent = ({
    eventName,
    properties,
  }: SendTrackingEventProps) => {
    if (isTrackingEnabled && !!envId) {
      sendTrackingEventMutation({ eventName, properties });
    }
  };

  return {
    sendTrackingEvent,
    initTracking,
    disableTracking,
    enableTracking,
    isTrackingEnabled,
    analytics,
  };
};

export default useTracking;
